/**
 * Shared vocabulary for assistant **onboardings** — guided kickoffs a page
 * hands to the assistant instead of showing a form. The panel grows to fill
 * the page, the assistant asks its questions, writes what it learns, and
 * finishes by calling the onboarding's terminal capability; the client keys
 * on that capability's result to collapse the panel again.
 *
 * ONE entry per onboarding: what both the web app and the API need. The API's
 * onboarding registry (`apps/api/src/assistant/onboarding/registry.ts`) owns
 * the prompt and the capability set, keyed by `id` — same split as
 * `ASSISTANT_PAGES` ↔ page descriptors.
 */

export const ASSISTANT_ONBOARDINGS = [
  {
    id: 'explore-kickoff',
    title: 'Set up exploration',
    /** The hidden user turn the client sends to open the thread. */
    kickoffText: 'Set up exploration for my application.',
    /** Capability whose successful result ends the onboarding. */
    finishCapabilityId: 'exploration.start',
  },
] as const;

export type AssistantOnboardingId = (typeof ASSISTANT_ONBOARDINGS)[number]['id'];

export type AssistantOnboardingInfo = (typeof ASSISTANT_ONBOARDINGS)[number];

export function isAssistantOnboardingId(value: string): value is AssistantOnboardingId {
  return ASSISTANT_ONBOARDINGS.some((onboarding) => onboarding.id === value);
}

export function assistantOnboarding(id: AssistantOnboardingId): AssistantOnboardingInfo {
  // `id` is derived from this same array, so the lookup cannot miss.
  return ASSISTANT_ONBOARDINGS.find((onboarding) => onboarding.id === id)!;
}

/** `metadata.kind` on the kickoff user message — rendered as a divider, not a bubble. */
export const ONBOARDING_KICKOFF_MARKER_KIND = 'onboarding-kickoff' as const;

/**
 * `metadata.kind` on a kickoff user message sent for a plain "thread with
 * kickoff" (not a full captive onboarding takeover) — e.g. a page opening
 * the ordinary assistant panel and pre-seeding it with a hidden first turn.
 * Rendered as a divider, not a bubble, same as `ONBOARDING_KICKOFF_MARKER_KIND`.
 */
export const THREAD_KICKOFF_MARKER_KIND = 'thread-kickoff' as const;
